  // js/helper/throttle.js
  function throttle(callback, delay3 = 15) {
    let throttleTimeout = null, storedEvent = null;
    const throttledEventHandler = (event) => {
      storedEvent = event;
      const shouldHandleEvent = !throttleTimeout;
      if (shouldHandleEvent) {
        callback(storedEvent);
        storedEvent = null;
        throttleTimeout = setTimeout(() => {
          throttleTimeout = null;
          if (storedEvent) {
            throttledEventHandler(storedEvent);
          }
        }, delay3);
      }
    };
    return throttledEventHandler;
  }

  // js/helper/event.js
  function triggerEvent(element, name, data = {}) {
    element.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      detail: data
    }));
  }
  function triggerNonBubblingEvent(element, name, data = {}) {
    element.dispatchEvent(new CustomEvent(name, {
      bubbles: false,
      detail: data
    }));
  }


  // js/custom-element/custom-html-element.js
  var CustomHTMLElement = class extends HTMLElement {
    constructor() {
      super();
      this._hasSectionReloaded = false;
      if (Shopify.designMode) {
        this.rootDelegate.on("shopify:section:select", (event) => {
          const parentSection = this.closest(".shopify-section");
          if (event.target === parentSection && event.detail.load) {
            this._hasSectionReloaded = true;
          }
        });
      }
    }
    get rootDelegate() {
      return this._rootDelegate = this._rootDelegate || new main_default(document.documentElement);
    }
    get delegate() {
      return this._delegate = this._delegate || new main_default(this);
    }
    showLoadingBar() {
      triggerEvent(document.documentElement, "theme:loading:start");
    }
    hideLoadingBar() {
      triggerEvent(document.documentElement, "theme:loading:end");
    }
    untilVisible(intersectionObserverOptions = { rootMargin: "30px 0px", threshold: 0 }) {
      const onBecameVisible = () => {
        this.classList.add("became-visible");
        this.style.opacity = "1";
      };
      return new Promise((resolve) => {
        if (window.IntersectionObserver) {
          this.intersectionObserver = new IntersectionObserver((event) => {
            if (event[0].isIntersecting) {
              this.intersectionObserver.disconnect();
              requestAnimationFrame(() => {
                resolve();
                onBecameVisible();
              });
            }
          }, intersectionObserverOptions);
          this.intersectionObserver.observe(this);
        } else {
          resolve();
          onBecameVisible();
        }
      });
    }
    disconnectedCallback() {
      var _a;
      this.delegate.destroy();
      this.rootDelegate.destroy();
      (_a = this.intersectionObserver) == null ? void 0 : _a.disconnect();
      delete this._delegate;
      delete this._rootDelegate;
    }
  };

// js/custom-element/ui/tabs-nav.js
  var TabsNav = class extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll("button[aria-controls]"));
      this.scrollerElement = this.querySelector(".tabs-nav__scroller");
      this.buttons.forEach((button) => button.addEventListener("click", () => this.selectButton(button)));
      this.addEventListener("shopify:block:select", (event) => this.selectButton(event.target, !event.detail.load));
      this.positionElement = document.createElement("span");
      this.positionElement.classList.add("tabs-nav__position");
      this.buttons[0].parentElement.insertAdjacentElement("afterend", this.positionElement);
      window.addEventListener("resize", this._onWindowResized.bind(this));
      this._adjustNavigationPosition();
      if (this.hasArrows) {
        this._handleArrows();
      }
    }
    get hasArrows() {
      return this.hasAttribute("arrows");
    }
    get selectedTabIndex() {
      return this.buttons.findIndex((button) => button.getAttribute("aria-expanded") === "true");
    }
    get selectedButton() {
      return this.buttons.find((button) => button.getAttribute("aria-expanded") === "true");
    }
    selectButton(button, animate = true) {
      if (!this.buttons.includes(button) || this.selectedButton === button) {
        return;
      }
      const from = document.getElementById(this.selectedButton.getAttribute("aria-controls")), to = document.getElementById(button.getAttribute("aria-controls"));
      if (animate) {
        this._transitionContent(from, to);
      } else {
        from.hidden = true;
        to.hidden = false;
      }
      this.selectedButton.setAttribute("aria-expanded", "false");
      button.setAttribute("aria-expanded", "true");
      triggerEvent(this, "tabs-nav:changed", { button });
      this._adjustNavigationPosition();
    }
    addButton(button) {
      button.addEventListener("click", () => this.selectButton(button));
      button.setAttribute("aria-expanded", "false");
      this.buttons[this.buttons.length - 1].insertAdjacentElement("afterend", button);
      this.buttons.push(button);
      this._adjustNavigationPosition(false);
    }
    _transitionContent(from, to) {
      from.animate({
        opacity: [1, 0]
      }, {
        duration: 250,
        easing: "ease"
      }).onfinish = () => {
        from.hidden = true;
        to.hidden = false;
        to.animate({
          opacity: [0, 1]
        }, {
          duration: 250,
          easing: "ease"
        });
      };
    }
    _onWindowResized() {
      this._adjustNavigationPosition();
    }
    _adjustNavigationPosition(shouldAnimate = true) {
      const scale = this.selectedButton.clientWidth / this.positionElement.parentElement.clientWidth, translate = this.selectedButton.offsetLeft / this.positionElement.parentElement.clientWidth / scale, windowHalfWidth = this.scrollerElement.clientWidth / 2;
      this.scrollerElement.scrollTo({
        behavior: shouldAnimate ? "smooth" : "auto",
        left: this.selectedButton.offsetLeft - windowHalfWidth + this.selectedButton.clientWidth / 2
      });
      if (!shouldAnimate) {
        this.positionElement.style.transition = "none";
      }
      this.positionElement.style.setProperty("--scale", scale);
      this.positionElement.style.setProperty("--translate", `${translate * 100}%`);
      this.positionElement.clientWidth;
      requestAnimationFrame(() => {
        this.positionElement.classList.add("is-initialized");
        this.positionElement.style.transition = null;
      });
    }
    _handleArrows() {
      const arrowsContainer = this.querySelector(".tabs-nav__arrows");
      arrowsContainer.firstElementChild.addEventListener("click", () => {
        this.selectButton(this.buttons[Math.max(this.selectedTabIndex - 1, 0)]);
      });
      arrowsContainer.lastElementChild.addEventListener("click", () => {
        this.selectButton(this.buttons[Math.min(this.selectedTabIndex + 1, this.buttons.length - 1)]);
      });
    }
  };
  window.customElements.define("tabs-nav", TabsNav);


    // js/custom-element/ui/scrollable-content.js
    var ScrollableContent = class extends CustomHTMLElement {
      connectedCallback() {
        if (this.draggable) {
          this._setupDraggability();
        }
        this._checkScrollability();
        window.addEventListener("resize", this._checkScrollability.bind(this));
        this.addEventListener("scroll", throttle(this._calculateProgress.bind(this), 15));
      }
      get draggable() {
        return this.hasAttribute("draggable");
      }
      _setupDraggability() {
        this.insertAdjacentHTML("afterend", '<drag-cursor hidden class="custom-drag-cursor"></drag-cursor>');
        const mediaQuery = matchMedia("(hover: none)");
        mediaQuery.addListener(this._onMediaChanges.bind(this));
        if (!mediaQuery.matches) {
          this._attachDraggableListeners();
        }
      }
      _attachDraggableListeners() {
        this.delegate.on("mousedown", this._onMouseDown.bind(this));
        this.delegate.on("mousemove", this._onMouseMove.bind(this));
        this.delegate.on("mouseup", this._onMouseUp.bind(this));
      }
      _removeDraggableListeners() {
        this.delegate.off("mousedown");
        this.delegate.off("mousemove");
        this.delegate.off("mouseup");
      }
      _checkScrollability() {
        this.classList.toggle("is-scrollable", this.scrollWidth > this.offsetWidth);
      }
      _calculateProgress() {
        const scrollLeft = this.scrollLeft * (window.themeVariables.settings.direction === "ltr" ? 1 : -1);
        const progress = Math.max(0, Math.min(1, scrollLeft / (this.scrollWidth - this.clientWidth))) * 100;
        triggerEvent(this, "scrollable-content:progress", { progress });
      }
      _onMediaChanges(event) {
        if (!event.matches) {
          this._attachDraggableListeners();
        } else {
          this._removeDraggableListeners();
        }
      }
      _onMouseDown(event) {
        if (event.target && event.target.nodeName === "IMG") {
          event.preventDefault();
        }
        this.startX = event.clientX + this.scrollLeft;
        this.diffX = 0;
        this.drag = true;
      }
      _onMouseMove(event) {
        if (this.drag) {
          this.diffX = this.startX - (event.clientX + this.scrollLeft);
          this.scrollLeft += this.diffX;
        }
      }
      _onMouseUp() {
        this.drag = false;
        let start = 1;
        let animate = () => {
          let step = Math.sinh(start);
          if (step <= 0) {
            window.cancelAnimationFrame(animate);
          } else {
            this.scrollLeft += this.diffX * step;
            start -= 0.03;
            window.requestAnimationFrame(animate);
          }
        };
        animate();
      }
    };
    window.customElements.define("scrollable-content", ScrollableContent);


    (() => {
      document.querySelectorAll(".rte table").forEach((table) => {
        table.outerHTML = '<div class="table-wrapper">' + table.outerHTML + "</div>";
      });
      document.querySelectorAll(".rte iframe").forEach((iframe) => {
        if (iframe.src.indexOf("youtube") !== -1 || iframe.src.indexOf("youtu.be") !== -1 || iframe.src.indexOf("vimeo") !== -1) {
          iframe.outerHTML = '<div class="video-wrapper">' + iframe.outerHTML + "</div>";
        }
      });
    })();


    (function(){
      function randomNumberTimeBased(t){
        var e = t.min
        , r = t.max
        , n = t.selected_products
        , o = t.interval
        , i = t.productId;
        if (nullOrUndefined(i))
          for (var u = Li.getHandleFromUrl(), c = 0; c < n.length; c += 1)
            i = n[c].handle === u ? n[c].id : 888888888888;
        var a = (new Date).toISOString()
        , f = parseInt(a.substring(0, 4), 10)
        , l = parseInt(a.substring(5, 7), 10)
        , s = parseInt(a.substring(8, 10), 10)
        , p = parseInt(a.substring(11, 13), 10)
        , y = (f * l * s * Math.ceil(p / o) + i) % r / r;
        return Math.floor(y * (r - e + 1)) + e
      }
      function nullOrUndefined(t){
        return null == t
      } 
       function getFlashBar(productID){
        return randomNumberTimeBased({
          min: 4,
          max: 20,
          selected_products: [],
          interval: 24,
          productId: productID
        });
      }
      document.querySelectorAll(".flash-deals-bar_top_bar[data-productid]").forEach(function(el, index){
        var productId = el.getAttribute('data-productid');
        var barVal = getFlashBar(""+productId);
        el.style.width = barVal+"%";
      });  
    })();



    (function(){
      var obj = document.querySelector('#product-grid[flash_deals_time],.q_product_box[flash_deals_time],.q_product_box1[flash_deals_time]');
      if(obj){
        setTime();
        function setTime() {
          var servicedatenow = new Date();
          var timeO = document.querySelectorAll('.flash_deals_box-right');
          var timeEnd1 = +new Date(obj.getAttribute('flash_deals_time'));
          var text = obj.getAttribute('flash_deals_text')
          var cycle = +obj.getAttribute('flash_deals_cycle_days');
          if(cycle && !isNaN(cycle)){
            timeEnd1 = setEndTime(timeEnd1, servicedatenow, cycle);
          }
          var qq = new QcountDown();
          qq.init(timeEnd1);
          qq.update(function (e) {
            timeO.forEach(function(el, i){
              el.innerText = text+' '+e.D+' : '+e.H+' : '+e.M+' : '+ e.S;
            });
          })
          qq.finish(function (e) {
          });
        }
        function setEndTime(time, servicedatenow, cycle){
          var timer = new Date(time);
          if(servicedatenow > timer){
            return setEndTime(timer.setDate(timer.getDate()+cycle), servicedatenow, cycle)
          }
          return timer
        }
      }
    })();